import React, { useRef } from 'react';

import { getActiveInteraction } from '@atlaskit/react-ufo/interaction-metrics';
import UFOInteractionIgnore from '@atlaskit/react-ufo/interaction-ignore';

type ExcludedInteraction =
	| 'edit-page.page_load'
	| 'edit-page.transition'
	| 'live-page.page_load'
	| 'live-page.transition';

export const DEFAULT_EXCLUDED_INTERACTIONS: ExcludedInteraction[] = [
	'edit-page.page_load',
	'edit-page.transition',
	'live-page.page_load',
	'live-page.transition',
];

type Props = React.PropsWithChildren<{
	excludedInteractions?: ExcludedInteraction[];
}>;

/**
 * Conditionally ignores UFO interaction holds for non-critical UI.
 *
 * Captures the active interaction at mount time to ensure stable behaviour for
 * the component's lifetime. `getActiveInteraction()` can return null mid-render
 * cycle if the UFO interaction ends, which would cause the `ignore` prop to
 * toggle between renders and effectively un-ignore the hold on a subsequent
 * re-render.
 *
 * The caller is responsible for providing the excluded interaction types and
 * experience keys, and for guarding usage behind the appropriate feature gate.
 */
export function ConditionalUfoHoldIgnore({
	children,
	excludedInteractions = DEFAULT_EXCLUDED_INTERACTIONS,
}: Props) {
	// Capture the interaction once at mount time to ensure stable behavior for the
	// component's lifetime. getActiveInteraction() can return null mid-render cycle
	// if the UFO interaction ends, which would cause the ignore prop to toggle
	// between renders and effectively un-ignore the hold on a subsequent re-render.
	const ignoreHoldsRef = useRef<boolean | null>(null);

	if (ignoreHoldsRef.current === null) {
		const interaction = getActiveInteraction();
		if (interaction) {
			const { type, ufoName } = interaction;
			ignoreHoldsRef.current = excludedInteractions.includes(
				`${ufoName}.${type}` as ExcludedInteraction,
			);
		} else {
			ignoreHoldsRef.current = false;
		}
	}

	const ignoreHolds = ignoreHoldsRef.current ?? false;

	return <UFOInteractionIgnore ignore={ignoreHolds}>{children}</UFOInteractionIgnore>;
}
